/* eslint-disable react-refresh/only-export-components */
import React, { useMemo } from 'react';
import { callBackendCommand, firebaseFunctionCommands } from '@/backend';
import { useAppStore } from '../../../store/useAppStore';
import { Button, Icon, Badge, IconButton } from '../../../components/common';
import { logger } from '../../../utils/logger';
import { DEFAULT_ROLES } from '../../auth/permissions';
import { getProductionTitle } from '../../invitations/invite-draft';

interface WelcomeCardWidgetProps {
    onDismiss: () => void;
}

const ROLE_ICONS: Record<string, string> = {
    'stage-manager': 'assignment_ind',
    cast: 'theater_comedy',
    actor: 'theater_comedy',
    director: 'movie',
    'sound-designer': 'headphones',
    'lighting-designer': 'lightbulb',
    'props-master': 'handyman',
    'costume-designer': 'checkroom',
    'production-manager': 'work',
};

function buildWelcomeDismissalId(input: {
    orgId?: string | null;
    projectId?: string | null;
    memberUserId?: string | null;
}): string | null {
    const orgId = input.orgId?.trim();
    const projectId = input.projectId?.trim();
    const memberUserId = input.memberUserId?.trim();
    if (!orgId || !projectId || !memberUserId) return null;
    return `${orgId}_${projectId}_${memberUserId}`;
}

export const WelcomeCardWidget: React.FC<WelcomeCardWidgetProps> = ({ onDismiss }) => {
    const user = useAppStore((s) => s.auth.user);
    const member = useAppStore((s) => s.cloud.member);
    const orgId = useAppStore((s) => s.cloud.orgId);
    const projectId = useAppStore((s) => s.cloud.projectId);
    const roles = useAppStore((s) => s.admin.roles);
    const personnel = useAppStore((s) => s.personnel);
    const showTitle = useAppStore((s) => s.projectMeta.title);

    const effectiveRoles = roles.length > 0 ? roles : DEFAULT_ROLES;
    const role = member ? effectiveRoles.find((r) => r.id === member.roleId) : null;
    const linkedPersonnel = member?.personnelId
        ? personnel.find((person) => person.id === member.personnelId && person.deleted !== true)
        : undefined;
    const productionTitle = linkedPersonnel ? getProductionTitle(linkedPersonnel) : undefined;
    const roleIcon = ROLE_ICONS[role?.id ?? ''] || 'badge';
    const displayName = user?.displayName?.split(' ')[0] || linkedPersonnel?.name?.split(' ')[0] || 'there';
    const isActor = role?.id === 'cast' || linkedPersonnel?.category === 'cast';
    const dismissalId = useMemo(() => buildWelcomeDismissalId({
        orgId,
        projectId,
        memberUserId: member?.userId,
    }), [orgId, projectId, member?.userId]);

    const handleDismiss = async () => {
        if (!user || !dismissalId || !orgId || !projectId || !member?.userId) {
            onDismiss();
            return;
        }

        try {
            await callBackendCommand(firebaseFunctionCommands.dismissProjectWelcomeCard, {
                orgId,
                projectId,
                memberUserId: member.userId,
                memberRoleId: member.roleId,
                personnelId: member.personnelId,
            });
        } catch (error: unknown) {
            logger.error('Failed to persist project welcome dismissal:', error);
        }

        onDismiss();
    };

    const firstActions = isActor
        ? ['Complete your profile', 'Check your calls', 'Read announcements']
        : ['Review your dashboard', 'Check the feed', 'Confirm your schedule'];

    return (
        <div
            className="relative overflow-hidden rounded-lg border border-primary/20 bg-white p-6 shadow-sm dark:bg-surface-dark"
            data-testid="welcome-card"
        >
            <IconButton
                aria-label="Dismiss welcome card"
                className="absolute right-3 top-3 flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                icon="close"
                label="Dismiss welcome card"
                onClick={() => { void handleDismiss(); }}
                title="Dismiss welcome card"
            />

            <div className="flex items-start gap-4 pr-10">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                    <Icon name={roleIcon} className="text-2xl text-primary" />
                </div>

                <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        Welcome, {displayName}
                    </h3>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                        {productionTitle ? (
                            <span className="text-gray-600 dark:text-gray-300">
                                Production title: <span className="font-medium text-gray-900 dark:text-white">{productionTitle}</span>
                            </span>
                        ) : null}
                        {role ? (
                            <span className="text-gray-600 dark:text-gray-300">
                                App access: <Badge variant="primary">{role.name}</Badge>
                            </span>
                        ) : null}
                        {showTitle ? (
                            <span className="text-gray-500 dark:text-gray-400">
                                Project: <span className="font-medium text-gray-700 dark:text-gray-300">{showTitle}</span>
                            </span>
                        ) : null}
                    </div>

                    <p className="mt-4 text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        {isActor
                            ? 'Completing your profile is critical so stage management has pronouns, phone, emergency contact, and contact details before rehearsal communication begins.'
                            : 'Your dashboard is scoped to this project and your app access. Start with the feed, your schedule, and any project-specific tools available to your role.'}
                    </p>

                    <div className="mt-4 grid gap-2 sm:grid-cols-3">
                        {firstActions.map((action) => (
                            <div
                                className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                                key={action}
                            >
                                {action}
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 flex items-center gap-3">
                        <Button variant="primary" size="sm" onClick={() => { void handleDismiss(); }}>
                            Got it
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export function useShowWelcomeCard(): boolean {
    const user = useAppStore((s) => s.auth.user);
    const member = useAppStore((s) => s.cloud.member);
    const orgId = useAppStore((s) => s.cloud.orgId);
    const projectId = useAppStore((s) => s.cloud.projectId);

    if (!user || !member) return false;
    if (user.isSuperAdmin) return false;

    const dismissalId = buildWelcomeDismissalId({
        orgId,
        projectId,
        memberUserId: member.userId,
    });
    if (dismissalId && user.projectWelcomeDismissals?.[dismissalId]) return false;

    return true;
}
